import React, { useRef } from 'react';
import AsyncSelect from 'react-select/async';
import { getResponsiveFs } from './ResponsiveWidth';

// Debounce utility for promises
function debouncePromise(fn, delay) {
  let timeoutId;
  let lastReject;
  return (...args) =>
    new Promise((resolve, reject) => {
      if (timeoutId) clearTimeout(timeoutId);
      if (lastReject) lastReject();
      lastReject = reject;
      timeoutId = setTimeout(() => {
        fn(...args)
          .then(resolve)
          .catch(reject);
      }, delay);
    });
}

function AsyncAutocompleteSelect({
  autoCompleteFn,
  mappedFn,
  onChange,
  value,
  displayOption = 'label',
  formatOptionLabelCustom = null,
  inputWidth = '250px',
  inputIcon = null,
  rightKey = null,
  debounceDelay = 600,
  defaultShowMenu = false,
  ...props
}) {
  const menuPortalTarget =
    typeof document !== 'undefined' ? document.body : null;
  const responsiveFs = getResponsiveFs();

  const formatOptionLabel = (option, { context }) => {
    // when rendering the “selected” value in the input:
    if (context === 'value') {
      return (
        <div className="d-flex align-items-center gap-1">
          <div className="d-flex align-items-center gap-1">
            {inputIcon && inputIcon}
            <span className="fw-normal">{option.label}</span>{' '}
          </div>

          <span
            style={{
              color: option.color || '#475467',
            }}
          >
            {rightKey && '#'}
            {rightKey ? option?.[rightKey] || option.value : null}
          </span>
        </div>
      );
    }
    // when rendering the menu list:
    if (displayOption === 'value') return option.value;
    if (displayOption === 'both') return `${option.label} | ${option.value}`;
    if (displayOption === 'bothCustom') {
      return (
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center', // keeps your other items centered
          }}
        >
          <div
            style={{
              width: '70%',
            }}
          >
            <span>{option.label}</span>
          </div>
          <span
            style={{
              width: '1px',
              backgroundColor: option.color || '#ccc',
              alignSelf: 'stretch',
            }}
          />
          <span style={{ opacity: 0.6, marginLeft: '10px' }}>
            {rightKey ? option?.[rightKey] : option.value}
          </span>
        </div>
      );
    }
    /* else */ return option.label;
  };

  const selectStyles = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    control: (base, state) => ({
      ...base,
      width: inputWidth,
      borderRadius: '8px',
      backgroundColor: state.isDisabled ? '#e9ecef' : '#fff',
      borderColor: state.isFocused ? '#86b7fe' : '#929398',
      boxShadow: state.isFocused
        ? '0 0 0 0.15rem rgba(13,110,253,.25)'
        : 'none',
      transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',
      '&:hover': { borderColor: state.isFocused ? '#86b7fe' : '#ced4da' },
      cursor: state.isDisabled ? 'not-allowed' : 'default',
      fontSize: responsiveFs,
    }),
    valueContainer: base => ({
      ...base,
      padding: '0.255rem 0.75rem',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? '#212529' : '#212529',
      paddingRight: '0.75rem',
      paddingLeft: 0,
      '&:hover': { color: '#212529' },
    }),
    menu: base => ({
      ...base,
      borderRadius: '0.375rem',
      overflow: 'hidden',
      boxShadow: '0 .5rem 1rem rgba(0,0,0,.15)',
      border: '1px solid #ced4da',
    }),
    placeholder: base => ({
      ...base,
      color: '#6c757d',
    }),
    singleValue: base => ({
      ...base,
      color: '#212529',
    }),
    container: base => ({
      ...base,
      width: inputWidth,
    }),
  };

  // Debounced loadOptions
  const debouncedLoadOptions = useRef(
    debouncePromise(async inputValue => {
      if (!inputValue && !defaultShowMenu) return [];
      const data = await autoCompleteFn(inputValue);
      return mappedFn(data);
    }, debounceDelay),
  ).current;

  return (
    <AsyncSelect
      cacheOptions
      defaultOptions
      loadOptions={debouncedLoadOptions}
      onChange={onChange}
      className="basic-single"
      classNamePrefix="select"
      isClearable={props.isClearable ?? true}
      isSearchable
      placeholder={props.placeholder || 'Search...'}
      value={value}
      menuPortalTarget={menuPortalTarget}
      styles={selectStyles}
      formatOptionLabel={formatOptionLabelCustom ?? formatOptionLabel}
      isDisabled={props.isDisabled ?? false}
      title={props.title || 'Select an option'}
      components={{
        DropdownIndicator: null,
        IndicatorSeparator: null,
        LoadingMessage: () => null,
        LoadingIndicator: () => null,
      }}
      noOptionsMessage={() => null}
    />
  );
}

export default AsyncAutocompleteSelect;
